import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Lock, User, MapPin, ArrowLeft } from "lucide-react";
import EditableText from "../components/EditableText";
import { PasswordRules } from "../components/PasswordRules";
import { passwordError } from "../lib/passwordPolicy";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    province: ""
  });
  const [provinces, setProvinces] = useState([]);
  const [checkEmail, setCheckEmail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const response = await axios.get(`${API}/provinces`);
        setProvinces(response.data);
      } catch (error) {
        console.error("Failed to fetch provinces");
      }
    };
    fetchProvinces();
  }, []);

  if (user) {
    if (user.requires_choice) navigate("/membership/choose");
    else if (user.verification_status !== "verified" && !user.is_admin) navigate("/verification");
    else navigate("/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    const pwErr = passwordError(formData.password);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }

    setLoading(true);

    try {
      const res = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        province: formData.province
      });
      if (res?.verification_required) {
        // Beta checkout intent must survive the email link opening in a new tab.
        const intent = sessionStorage.getItem("dhdh_beta_intent");
        if (intent) localStorage.setItem("dhdh_beta_intent", intent);
        setCheckEmail(res.email);
        return;
      }

      // If the visitor came in via beta redemption + clicked a tier on the
      // launch landing, fast-track them through Stripe checkout. Backend
      // forces RDH Pro tier + founding rate regardless of tier picked.
      const betaIntentRaw = sessionStorage.getItem("dhdh_beta_intent");
      if (betaIntentRaw) {
        sessionStorage.removeItem("dhdh_beta_intent");
        try {
          const intent = JSON.parse(betaIntentRaw);
          const planKey = `${intent.tier}_${intent.interval || "annual"}`;
          toast.success("Account created — taking you to checkout…");
          const res = await axios.post(`${API}/payments/subscribe`, {
            plan: planKey,
            origin_url: window.location.origin,
          });
          if (res.data.checkout_url || res.data.url) {
            window.location.href = res.data.checkout_url || res.data.url;
            return;
          }
        } catch (sErr) {
          toast.error(sErr.response?.data?.detail || "Couldn't start checkout — pick a plan from your account.");
          navigate("/membership");
          return;
        }
      }

      toast.success("Account created! Pick your membership.");
      navigate("/membership/choose");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (checkEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-4">
        <div className="max-w-md w-full bg-white border border-[#E5DFEC] rounded-2xl p-8 text-center" data-testid="register-check-email">
          <Mail className="w-10 h-10 mx-auto text-[#6B4E8A]" />
          <h1 className="mt-4 text-2xl text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>Check your inbox</h1>
          <p className="mt-2 text-sm text-[#64748B]">We sent a verification link to <strong>{checkEmail}</strong>. Click it within 24 hours to activate your account.</p>
          <Button
            variant="outline"
            className="mt-6 rounded-full"
            data-testid="register-resend-verification-btn"
            onClick={async () => {
              try { const { data } = await axios.post(`${API}/auth/resend-verification`, { email: checkEmail }); toast.success(data.message); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't resend"); }
            }}
          >
            Resend link
          </Button>
          <p className="mt-4 text-xs text-[#94A3B8]">Already verified? <Link to="/login" className="text-[#6B4E8A] underline">Sign in</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex flex-1 bg-[#6B4E8A] items-center justify-center p-16">
        <div className="max-w-md text-center">
          <img src="/dhdh-logo.png" alt="DHDH" className="w-32 h-32 rounded-full object-cover mx-auto mb-8 ring-4 ring-white/30" />
          <h2 className="text-3xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Join the Community
          </h2>
          <p className="text-[#94A3B8]">
            Connect with dental hygienists across Canada. Share knowledge, grow professionally, and make lasting connections.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center text-[#64748B] hover:text-[#6B4E8A] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="mb-8">
            <EditableText
              blockKey="register.title"
              fallback="Create your account"
              as="h1"
              className="text-3xl font-medium text-[#6B4E8A] mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="register.subtitle"
              fallback="Join the DHDH community today"
              as="p"
              className="text-[#64748B]"
              multiline
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#6B4E8A] font-medium">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Jane Smith, RDH"
                  className="pl-12 py-5 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#6B4E8A] font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="pl-12 py-5 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="province" className="text-[#6B4E8A] font-medium">Province</Label>
              <div className="relative mt-2">
                <Select
                  value={formData.province}
                  onValueChange={(value) => setFormData({ ...formData, province: value })}
                >
                  <SelectTrigger className="py-5 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] rounded-xl" data-testid="register-province-select">
                    <div className="flex items-center">
                      <MapPin className="w-5 h-5 text-[#64748B] mr-3" />
                      <SelectValue placeholder="Select your province" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#6B4E8A] font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-12 py-5 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <PasswordRules password={formData.password} testId="register-password-rules" />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#6B4E8A] font-medium">Confirm Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="pl-12 py-5 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full py-6 text-base font-medium mt-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>
            <p className="text-xs text-[#64748B] text-center mt-3 leading-relaxed" data-testid="register-legal-ack">
              By creating an account you agree to our{" "}
              <a href="/terms" className="text-[#8DB5A0] hover:underline">Terms of Service</a>{" "}
              and{" "}
              <a href="/privacy" className="text-[#8DB5A0] hover:underline">Privacy Policy</a>.
            </p>
            <p className="text-[11px] text-[#94A3B8] text-center mt-2 leading-relaxed" data-testid="register-platform-disclaimer">
              DHDH is a peer community platform. Content shared by members does not constitute clinical, legal, or financial advice. Always consult a licensed professional.
            </p>
          </form>

          <p className="text-center text-[#64748B] mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#8DB5A0] hover:text-[#D0694E] font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
